// ZEIT-HELFER-ADMIN-001 — EINE Stelle, an der aus einem Server-Zeitwert Anzeige wird.
//
// Der Server liefert Zeitwerte in zwei Formaten: ISO mit `Z` (neu) und das rohe
// SQLite-Format „YYYY-MM-DD HH:MM:SS" (Altbestand). Das rohe Format hat KEINE
// Zeitzonenkennung, ist aber UTC. `new Date('2026-09-03 19:30:00')` liest es als
// ORTSzeit — die Anzeige lag damit zwei Stunden zu früh, und ab 22:00 Berlin sogar
// einen Tag daneben. Genau dieser Fehler stand an 30 Stellen im Admin.
//
// Die Lösung war im Kundenprofil (pages/Users.js, `fmtDbDate`) schon richtig gebaut, aber
// nirgends sonst benutzt. Hier steht sie jetzt EINMAL — jede Seite ruft sie auf.
// Halbfix-Falle: `replace(' ', 'T')` OHNE angehängtes `Z` bleibt Ortszeit und ist genauso falsch.
//
// Gleiche API und gleiches Verhalten wie partner-portal/src/lib/zeit.js (ZEIT-EINHEIT-PORTAL-001),
// ergänzt um: Millisekunden-Zahlen in `alsDatum`, Optionen `jahr`/`sekunden`/`leer`.
//
// Grundsatz: gerechnet wird in UTC, angezeigt wird IMMER in Europe/Berlin.

export const ZEITZONE = 'Europe/Berlin';

// Server-Zeitwert (ISO mit Kennung ODER SQLite-UTC ohne), Date oder Millisekunden → Date. Ungültig → null.
export function alsDatum(wert) {
  if (wert instanceof Date) return Number.isNaN(wert.getTime()) ? null : wert;
  if (typeof wert === 'number') return Number.isFinite(wert) ? new Date(wert) : null;
  if (wert == null) return null;
  const roh = String(wert).trim();
  if (!roh) return null;
  const kandidat = roh.includes('T') ? roh : roh.replace(' ', 'T');
  const hatZone = /(Z|[+-]\d{2}:?\d{2})$/.test(kandidat);
  const datum = new Date(hatZone ? kandidat : `${kandidat}Z`);
  return Number.isNaN(datum.getTime()) ? null : datum;
}

// Ein `datetime-local`-Wert ist eine Berliner Wandzeit ohne Zonenkennung. Er darf deshalb
// weder als UTC noch in der zufälligen Browser-Zeitzone gelesen werden. Diese Umrechnung
// betrifft nur bestehende manuell angelegte Rabatte; die gespeicherte Schreibform bleibt
// unverändert. Nicht existente Uhrzeiten beim DST-Sprung werden fail-closed abgelehnt.
const WANDZEIT = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?)?$/;
const wandzeitTeile = (datum) => new Intl.DateTimeFormat('en-CA', {
  timeZone: ZEITZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
}).formatToParts(datum).reduce((acc, teil) => {
  if (teil.type !== 'literal') acc[teil.type] = Number(teil.value);
  return acc;
}, {});

export function berlinerWandzeitAlsDatum(wert) {
  const match = WANDZEIT.exec(String(wert || '').trim());
  if (!match) return null;
  const ziel = match.slice(1, 7).map((teil, index) => (teil == null && index >= 3 ? 0 : Number(teil)));
  const millisekunden = Number((match[7] || '').padEnd(3, '0'));
  const alsUtc = (teile) => Date.UTC(teile[0], teile[1] - 1, teile[2], teile[3], teile[4], teile[5] || 0);
  const zielMs = alsUtc(ziel);
  const normalisiert = new Date(zielMs);
  if (normalisiert.getUTCFullYear() !== ziel[0]
      || normalisiert.getUTCMonth() + 1 !== ziel[1]
      || normalisiert.getUTCDate() !== ziel[2]
      || normalisiert.getUTCHours() !== ziel[3]
      || normalisiert.getUTCMinutes() !== ziel[4]
      || normalisiert.getUTCSeconds() !== ziel[5]) return null;
  let instant = zielMs;
  for (let versuch = 0; versuch < 3; versuch += 1) {
    const teile = wandzeitTeile(new Date(instant));
    const angezeigt = [teile.year, teile.month, teile.day, teile.hour, teile.minute, teile.second];
    const differenz = zielMs - alsUtc(angezeigt);
    if (differenz === 0) return new Date(instant + millisekunden);
    instant += differenz;
  }
  return null;
}

// Bestehende Eingabeverträge erlauben sowohl Berliner Wandzeit (datetime-local/Agent-Text)
// als auch einen explizit zonierten ISO-Zeitpunkt. Nur zonenlose Werte werden als Berliner
// Wandzeit interpretiert; Z/Offset behalten ihren absoluten Zeitpunkt samt Millisekunden.
export function wandzeitOderInstantAlsDatum(wert) {
  const text = String(wert || '').trim();
  return /(Z|[+-]\d{2}:?\d{2})$/.test(text) ? alsDatum(text) : berlinerWandzeitAlsDatum(text);
}

// `leer` ist der Platzhalter für fehlende/ungültige Werte — Standard „—", damit sich die
// Aufrufstellen ihren bisherigen Text („–", „", „nicht verfügbar") erhalten können.
const teile = (wert, optionen, leer = '—') => {
  const datum = alsDatum(wert);
  return datum ? datum.toLocaleString('de-DE', { timeZone: ZEITZONE, ...optionen }) : leer;
};

// „21:30" · mit `sekunden: true` „21:30:15"
export const uhrzeit = (wert, { sekunden = false, leer } = {}) => teile(wert, {
  hour: '2-digit', minute: '2-digit', ...(sekunden ? { second: '2-digit' } : {}),
}, leer);

// „03.09.26, 21:30" (Standard, wie im Partner-Portal)
//   jahr: 'kurz' (Standard) → „03.09.26, 21:30" · 'voll' → „03.09.2026, 21:30" · false → „03.09., 21:30"
//   sekunden: true → zusätzlich „:15"
const JAHR = { kurz: { year: '2-digit' }, voll: { year: 'numeric' } };
export const datumUndZeit = (wert, { jahr = 'kurz', sekunden = false, leer } = {}) => teile(wert, {
  day: '2-digit', month: '2-digit', ...(JAHR[jahr] || {}),
  hour: '2-digit', minute: '2-digit', ...(sekunden ? { second: '2-digit' } : {}),
}, leer);

// „03.09.2026" — auch für reine Kalendertage „2026-09-03": die werden als UTC-Mitternacht
// gelesen und liegen in Berlin am selben Tag; eine Phantom-Uhrzeit gibt es hier nicht.
export const nurDatum = (wert, { leer } = {}) => teile(wert, { day: '2-digit', month: '2-digit', year: 'numeric' }, leer);

// „03.09.2026, 21:30" — Abrechnungszeiträume (Payouts) — wie im Partner-Portal
export const datumLangUndZeit = (wert, { leer } = {}) => teile(wert, { dateStyle: 'medium', timeStyle: 'short' }, leer);

// „3. Sept., 21:30" — wie im Partner-Portal (Postfach)
export const datumKurzMitZeit = (wert, { leer } = {}) => teile(wert, {
  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
}, leer);

// „Do., 3. Sept., 21:30" — wie im Partner-Portal (Reservierungstermine)
export const wochentagKurzMitZeit = (wert, { leer } = {}) => teile(wert, {
  weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
}, leer);

// „3. September 2026" — Tagesüberschrift · mit `wochentag: true` „Donnerstag, 3. September 2026"
export const tagUeberschrift = (wert, { wochentag = false, leer } = {}) => teile(wert, {
  ...(wochentag ? { weekday: 'long' } : {}), day: 'numeric', month: 'long', year: 'numeric',
}, leer);

// „2026-09-03" nach BERLINER Kalendertag — der Schlüssel, nach dem gruppiert wird.
// `toISOString()` wäre hier falsch: es gruppiert nach UTC-Tag und zerschneidet den Abend.
export function tagSchluessel(wert) {
  const datum = alsDatum(wert);
  if (!datum) return '';
  const p = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZEITZONE, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(datum);
  return p;
}

// Berliner Kalendertag um ganze Tage verschieben. UTC dient hier nur als neutrale
// Kalenderarithmetik auf einem bereits bestimmten JJJJ-MM-TT-Schlüssel; dadurch kann
// weder die Gerätezeitzone noch eine Sommerzeitumstellung den Wochentag verschieben.
function tagVerschieben(tag, tage) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(tag || ''));
  if (!match) return '';
  const datum = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]) + tage));
  return datum.toISOString().slice(0, 10);
}

// Montag bis Sonntag der BERLINER Kalenderwoche, unabhängig davon, in welcher
// Zeitzone das Gerät oder der Browser gerade läuft.
export function berlinerKalenderwoche(wert = new Date()) {
  const heute = tagSchluessel(wert);
  if (!heute) return { from: '', to: '' };
  const [jahr, monat, tag] = heute.split('-').map(Number);
  const seitMontag = (new Date(Date.UTC(jahr, monat - 1, tag)).getUTCDay() + 6) % 7;
  return {
    from: tagVerschieben(heute, -seitMontag),
    to: tagVerschieben(heute, 6 - seitMontag),
  };
}

// Operative Kennzahlen verwenden den lokalen Kalendertag, der um 04:00 beginnt.
// Der Kalenderhelfer oben bleibt für Belege, Fristen und andere echte Kalenderdaten erhalten.
export function betriebstagSchluessel(wert = new Date()) {
  const datum = alsDatum(wert);
  if (!datum) return '';
  const teile = Object.fromEntries(new Intl.DateTimeFormat('en-CA', {
    timeZone: ZEITZONE, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', hourCycle: 'h23',
  }).formatToParts(datum).filter((teil) => teil.type !== 'literal').map((teil) => [teil.type, teil.value]));
  const tag = `${teile.year}-${teile.month}-${teile.day}`;
  return Number(teile.hour) < 4 ? tagVerschieben(tag, -1) : tag;
}

export function berlinerBetriebswoche(wert = new Date()) {
  const heute = betriebstagSchluessel(wert);
  if (!heute) return { from: '', to: '' };
  const [jahr, monat, tag] = heute.split('-').map(Number);
  const seitMontag = (new Date(Date.UTC(jahr, monat - 1, tag)).getUTCDay() + 6) % 7;
  return {
    from: tagVerschieben(heute, -seitMontag),
    to: tagVerschieben(heute, 6 - seitMontag),
  };
}

// Abstand zwischen zwei Ereignissen, wie im Vorbild: „37 Sek." · „13 Min." · „4 Std. 30 Min."
export function dauer(sekunden) {
  const s = Math.max(0, Math.round(Number(sekunden) || 0));
  if (s < 60) return `${s} Sek.`;
  const minuten = Math.floor(s / 60);
  if (minuten < 60) return `${minuten} Min.`;
  const stunden = Math.floor(minuten / 60);
  const rest = minuten % 60;
  return rest === 0 ? `${stunden} Std.` : `${stunden} Std. ${rest} Min.`;
}
